import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'react-final-form-arrays';
import { injectIntl, intlShape } from '../../util/reactIntl';
import css from './ListingAdditionalFeesField.module.css';
import IconCollection from '../IconCollection/IconCollection';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import ListingFieldRichText from '../ListingFieldRichText/ListingFieldRichText';
import { FEE_TYPE_FIELD, WHEN_TO_APPLY_FIELD, FEE_CALC_TYPE_FIELD, CHECKOUT_OPTIONS_FIELD, FEE_DESCRIPTION_FIELD, FLAT_RATE, PERCENTAGE_OF_RENTAL, USAGE_MULTIPLE } from '../../util/constants';
import FieldCheckbox from '../FieldCheckbox/FieldCheckbox';
import classNames from 'classnames';
import Chevron from '../Chevron/Chevron';

const FEE_DESCRIPTION_MAX_LENGTH = 50;

function FeeModal({ isOpen, onClose, onSubmit, initialValues, usedFeeTypes, feeTypeOptions, whenToApplyOptions, feeCalculationTypeOptions, checkoutOptions, intl, editingIdx }) {
    const [fee, setFee] = useState(initialValues || {});
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setFee(initialValues || {});

        return () => {
            setTouched({});
            setSubmitting(false);
        };
    }, [initialValues]);

    const handleChange = (field, value) => {
        setFee(f => ({ ...f, [field]: value }));
        setTouched(t => ({ ...t, [field]: true }));
    };

    const handleConfirm = () => {
        setSubmitting(true);
        // Validate required fields
        const requiredFields = [FEE_TYPE_FIELD, WHEN_TO_APPLY_FIELD, FEE_CALC_TYPE_FIELD, CHECKOUT_OPTIONS_FIELD, FEE_DESCRIPTION_FIELD];
        const missing = requiredFields.filter(f => !fee[f]);
        let errors = {};
        if (missing.length > 0) {
            missing.forEach(f => { errors[f] = true; });
        }
        // Conditionally required fields
        if (showRateField) {
            if (!fee.rate || isNaN(Number(fee.rate)) || Number(fee.rate) <= 0) {
                errors.rate = true;
            }
        }
        if (showPercentageField) {
            if (
                fee.percentage === undefined || fee.percentage === '' ||
                isNaN(Number(fee.percentage)) ||
                Number(fee.percentage) < 0 || Number(fee.percentage) > 100
            ) {
                errors.percentage = true;
            }
        }
        if (showLabelField) {
            if (!fee.usageLabel || fee.usageLabel.trim() === '') {
                errors.usageLabel = true;
            }
        }
        if (fee && fee[FEE_DESCRIPTION_FIELD] && fee[FEE_DESCRIPTION_FIELD].length > FEE_DESCRIPTION_MAX_LENGTH) {
            errors.feeDescription = true;
        }
        if (Object.keys(errors).length > 0) {
            setTouched(t => ({ ...t, ...errors }));
            setSubmitting(false);
            return;
        }
        onSubmit(fee);
        setSubmitting(false);
    };

    const showRateField = fee[FEE_CALC_TYPE_FIELD] === FLAT_RATE || fee[FEE_CALC_TYPE_FIELD] === USAGE_MULTIPLE;
    const showPercentageField = fee[FEE_CALC_TYPE_FIELD] === PERCENTAGE_OF_RENTAL;
    const showLabelField = fee[FEE_CALC_TYPE_FIELD] === USAGE_MULTIPLE;

    return (
        <Modal id="add-fee-modal" isOpen={isOpen} onClose={onClose} onManageDisableScrolling={() => { }}>
            <div className={css.modalContent}>
                <h3>{intl.formatMessage({ id: 'ListingAdditionalFeesField.modalTitle' })}</h3>
                <div className={css.modalFieldsGrid}>
                    <div className={css.modalFieldRow} style={{ gridColumn: '1 / span 2' }}>
                        <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.feeTypeLabel' })}</label>
                        <select value={fee.feeType || ''} onChange={e => handleChange('feeType', e.target.value)}>
                            <option value="">{intl.formatMessage({ id: 'ListingAdditionalFeesField.selectOption' })}</option>
                            {feeTypeOptions.map(opt => {
                                const isUsed = usedFeeTypes.includes(opt.option) && opt.option !== initialValues?.feeType;
                                return (
                                    <option key={opt.option} value={opt.option} disabled={isUsed}>{opt.label}</option>
                                );
                            })}
                        </select>
                        {touched.feeType && !fee.feeType && <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.required' })}</span>}
                    </div>
                    <div className={css.modalFieldRow}>
                        <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.whenToApplyLabel' })}</label>
                        <div className={css.radioGroupRow}>
                            {whenToApplyOptions.map(opt => (
                                <div className={css.radioGroupItem}>
                                    <input
                                        className={css.input}
                                        type="radio"
                                        name="whenToApply"
                                        value={opt.option}
                                        checked={fee.whenToApply === opt.option}
                                        onChange={e => handleChange('whenToApply', e.target.value)}
                                    />
                                    <label key={opt.option} className={css.label}>
                                        <span className={css.radioButtonWrapper}>

                                            <svg style={{ fill: 'transparent' }} className={css.unchecked} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 0.5C15.2467 0.5 19.5 4.7533 19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.7533 19.5 0.5 15.2467 0.5 10C0.5 4.7533 4.7533 0.5 10 0.5Z" stroke="#E9EAEB" />
                                            </svg>

                                            <svg className={css.checked} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#F2704E" />
                                                <path d="M6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10Z" fill="white" />
                                            </svg>
                                        </span>
                                        <span className={css.text}>
                                            {opt.label}
                                        </span>

                                    </label>
                                </div>
                            ))}
                        </div>
                        {touched.whenToApply && !fee.whenToApply && <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.required' })}</span>}
                    </div>
                    <div className={css.modalFieldRow}>
                        <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.feeCalculationTypeLabel' })}</label>
                        <div className={css.radioGroupRow}>
                            {feeCalculationTypeOptions.map(opt => (
                                <div className={css.radioGroupItem}>
                                    <input
                                        className={css.input}
                                        type="radio"
                                        name="feeCalculationType"
                                        value={opt.option}
                                        checked={fee.feeCalculationType === opt.option}
                                        onChange={e => handleChange('feeCalculationType', opt.option)}
                                    />
                                    <label key={opt.option} className={css.label}>
                                        <span className={css.radioButtonWrapper}>

                                            <svg style={{ fill: 'transparent' }} className={css.unchecked} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 0.5C15.2467 0.5 19.5 4.7533 19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.7533 19.5 0.5 15.2467 0.5 10C0.5 4.7533 4.7533 0.5 10 0.5Z" stroke="#E9EAEB" />
                                            </svg>

                                            <svg className={css.checked} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#F2704E" />
                                                <path d="M6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10Z" fill="white" />
                                            </svg>
                                        </span>
                                        <span className={css.text}>
                                            {opt.label}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                        {touched.feeCalculationType && !fee.feeCalculationType && <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.required' })}</span>}
                    </div>
                    {showRateField && (
                        <div className={css.modalFieldRow}>
                            <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.rateLabel' })}</label>
                            <input
                                type="number"
                                value={fee.rate || ''}
                                onChange={e => handleChange('rate', e.target.value)}
                                placeholder={intl.formatMessage({ id: 'ListingAdditionalFeesField.ratePlaceholder' })}
                            />
                            {touched.rate && (!fee.rate || isNaN(Number(fee.rate)) || Number(fee.rate) <= 0) && (
                                <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.rateError' })}</span>
                            )}
                        </div>
                    )}
                    {showLabelField && (
                        <div className={css.modalFieldRow}>
                            <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.usageLabelLabel' })}</label>
                            <input
                                type="text"
                                value={fee.usageLabel || ''}
                                onChange={e => handleChange('usageLabel', e.target.value)}
                                placeholder={intl.formatMessage({ id: 'ListingAdditionalFeesField.usageLabelPlaceholder' })}
                            />
                            {touched.usageLabel && (!fee.usageLabel || fee.usageLabel.trim() === '') && (
                                <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.required' })}</span>
                            )}
                        </div>
                    )}
                    {showPercentageField && (
                        <div className={css.modalFieldRow}>
                            <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.percentageLabel' })}</label>
                            <input
                                type="number"
                                value={fee.percentage || ''}
                                onChange={e => handleChange('percentage', e.target.value)}
                                placeholder={intl.formatMessage({ id: 'ListingAdditionalFeesField.percentagePlaceholder' })}
                            />
                            {touched.percentage && (
                                (fee.percentage === undefined || fee.percentage === '' || isNaN(Number(fee.percentage)) || Number(fee.percentage) < 0 || Number(fee.percentage) > 100)
                            ) && (
                                    <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.percentageError' })}</span>
                                )}
                        </div>
                    )}
                    <div className={css.modalFieldRow} style={{ gridColumn: '1 / span 2' }}>
                        <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.checkoutOptionsLabel' })}</label>
                        <div className={css.radioGroupRow}>
                            {checkoutOptions.map(opt => (
                                <div className={css.radioGroupItem}>
                                    <input
                                        className={css.input}
                                        type="radio"
                                        name="checkoutOptions"
                                        value={opt.option}
                                        checked={fee.checkoutOptions === opt.option}
                                        onChange={e => handleChange('checkoutOptions', opt.option)}
                                    />
                                    <label key={opt.option} className={css.label}>
                                        <span className={css.radioButtonWrapper}>

                                            <svg style={{ fill: 'transparent' }} className={css.unchecked} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 0.5C15.2467 0.5 19.5 4.7533 19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.7533 19.5 0.5 15.2467 0.5 10C0.5 4.7533 4.7533 0.5 10 0.5Z" stroke="#E9EAEB" />
                                            </svg>

                                            <svg className={css.checked} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#F2704E" />
                                                <path d="M6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10Z" fill="white" />
                                            </svg>
                                        </span>
                                        <span className={css.text}>
                                            {opt.label}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                        {touched.checkoutOptions && !fee.checkoutOptions && <span className={css.error}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.required' })}</span>}
                    </div>
                    <div className={css.modalFieldRow} style={{ gridColumn: '1 / span 2' }}>
                        <label>{intl.formatMessage({ id: 'ListingAdditionalFeesField.descriptionLabel' })}</label>
                        <textarea
                            type="text"
                            value={fee.feeDescription || ''}
                            onChange={e => handleChange('feeDescription', e.target.value)}
                            maxLength={FEE_DESCRIPTION_MAX_LENGTH}
                            placeholder={intl.formatMessage({ id: 'ListingAdditionalFeesField.descriptionPlaceholder' })}
                        />
                        <span className={css.helperText}>
                            {intl.formatMessage(
                                { id: 'ListingAdditionalFeesField.characterCount' },
                                { count: fee.feeDescription?.length || 0, max: FEE_DESCRIPTION_MAX_LENGTH }
                            )}
                        </span>
                        {touched.feeDescription && (!fee.feeDescription || fee.feeDescription.length > FEE_DESCRIPTION_MAX_LENGTH) && (
                            <span className={css.error}>
                                {intl.formatMessage(
                                    { id: 'ListingAdditionalFeesField.descriptionError' },
                                    { max: FEE_DESCRIPTION_MAX_LENGTH }
                                )}
                            </span>
                        )}
                    </div>
                </div>
                <div className={css.modalActions}>
                    <Button variant="info" type="button" onClick={onClose} className={css.cancelBtn}>
                        {intl.formatMessage({ id: 'ListingAdditionalFeesField.cancel' })}
                    </Button>
                    <Button variant="primary" type="button" onClick={handleConfirm} className={css.confirmBtn} disabled={submitting}>
                        {intl.formatMessage({ id: editingIdx !== null ? 'ListingAdditionalFeesField.update' : 'ListingAdditionalFeesField.confirm' })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

const ListingAdditionalFeesField = ({
    name,
    noteFieldName = 'additionalChargesNote',
    noteValue,
    onNoteChange,
    fields: additionalFees = [],
    showCredits,
    showRegenerate,
    showToggle = false,
    toggleValue,
    enabled,
    intl,
    generateInProgress,
    generateError,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editIdx, setEditIdx] = useState(null);
    const [editInitial, setEditInitial] = useState(null);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 767);

    const [expanded, setExpanded] = useState(false);
    // Option fallback for backward compatibility
    const FEE_TYPE_OPTIONS = additionalFees.find(f => f.key === FEE_TYPE_FIELD)?.enumOptions || [];
    const WHEN_TO_APPLY_OPTIONS = additionalFees.find(f => f.key === WHEN_TO_APPLY_FIELD)?.enumOptions || [];
    const FEE_CALC_TYPE_OPTIONS = additionalFees.find(f => f.key === FEE_CALC_TYPE_FIELD)?.enumOptions || [];
    const CHECKOUT_OPTIONS = additionalFees.find(f => f.key === CHECKOUT_OPTIONS_FIELD)?.enumOptions || [];

    React.useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 767);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const hideClass = expanded ? css.body : css.hidden;
    return (
        <FieldArray name={name}>
            {({ fields }) => {
                const usedFeeTypes = (fields.value || []).map(fee => fee?.feeType).filter(Boolean);
                return (
                    <div className={css.root}>
                        <div className={css.headerRow}>
                            <div>
                            <div className={css.headerTitle}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.title' })}</div>
                          
                            </div>
                            <div className={css.sectionControls}>
                                {showToggle && (
                                    <label className={css.toggleLabel}>
                                        <FieldCheckbox
                                            id={`${name}Enabled`}
                                            name={`${name}Enabled`}
                                            value={toggleValue}
                                            label="Enabled"
                                            className={css.toggleInput}
                                        />
                                        <span className={classNames(css.toggleSlider, { [css.enabled]: enabled })}></span>
                                    </label>
                                )}
                                {!isMobile && <Button size={"sm"} variant="ghost" type="button" className={classNames(css.addBtn, hideClass)} onClick={() => { setEditIdx(null); setEditInitial(null); setModalOpen(true); }}>
                                    <IconCollection name="plus_icon" width={18} height={18} /> {intl.formatMessage({ id: 'ListingAdditionalFeesField.addFee' })}
                                </Button>}
                                <div className={css.chevronBtn} onClick={() => { setExpanded(!expanded); }}>
                                    <Chevron expanded={expanded} />
                                </div>
                            </div>
                        </div>
                        <div className={classNames(css.label)}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.helperText' })}</div>
                        {isMobile && <Button size={"sm"} variant="ghost" type="button" className={classNames(css.addBtn, hideClass)} onClick={() => { setEditIdx(null); setEditInitial(null); setModalOpen(true); }}>
                                    <IconCollection name="plus_icon" width={18} height={18} /> {intl.formatMessage({ id: 'ListingAdditionalFeesField.addFee' })}
                                </Button>}
                        {fields.length > 0 && <div className={classNames(css.feeList, hideClass)}>
                            {fields.map((name, idx) => {
                                const fee = fields.value[idx];
                                
                                return (
                                    <div key={name} className={css.feeItem}>
                                        <div className={css.feeMainRow}>
                                            <div className={css.feeLabel}>
                                                {fee?.feeDescription || <span className={css.placeholder}>{intl.formatMessage({ id: 'ListingAdditionalFeesField.noDescription' })}</span>}
                                            </div>
                                            <div className={css.feeActions}>
                                                <Button type="button" className={css.iconBtn} onClick={() => { setEditIdx(idx); setEditInitial(fee); setModalOpen(true); }}>
                                                    <IconCollection name="icon_pencil" width={18} height={18} />
                                                </Button>
                                                <Button type="button" className={css.iconBtn} onClick={() => fields.remove(idx)}>
                                                    <IconCollection name="icon_trash" width={18} height={18} />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>}
                        <FeeModal
                            isOpen={modalOpen}
                            onClose={() => setModalOpen(false)}
                            initialValues={fields?.value?.[editIdx] || {}}
                            onSubmit={fee => {
                                if (editIdx !== null) {
                                    fields.update(editIdx, fee);
                                } else {
                                    fields.push(fee);
                                }
                                setModalOpen(false);
                            }}
                            usedFeeTypes={usedFeeTypes}
                            editingIdx={editIdx}
                            feeTypeOptions={FEE_TYPE_OPTIONS}
                            whenToApplyOptions={WHEN_TO_APPLY_OPTIONS}
                            feeCalculationTypeOptions={FEE_CALC_TYPE_OPTIONS}
                            checkoutOptions={CHECKOUT_OPTIONS}
                            intl={intl}
                        />
                        {/* Additional Charges - General Note */}
                        <div className={classNames(css.generalNote, hideClass)} style={{ marginTop: 32 }}>
                            <div className={css.headerTitle}>
                                {intl.formatMessage({ id: 'ListingAdditionalFeesField.generalNoteTitle' })}
                            </div>
                            <div className={css.label} >
                                {intl.formatMessage({ id: 'ListingAdditionalFeesField.generalNoteHelperText' })}
                            </div>
                            <div className={css.generalNoteContent}>
                          <ListingFieldRichText
                                name={noteFieldName}
                                label=""
                                placeholder={intl.formatMessage({ id: 'ListingAdditionalFeesField.generalNotePlaceholder' })}
                                maxLength={2000}
                                value={noteValue}
                                onChange={onNoteChange}
                                showCredits={showCredits}
                                showRegenerate={showRegenerate}
                                showToggle={showToggle}
                                showChevron={false}
                            />
                          </div>
                        </div>
                    </div>
                );
            }}
        </FieldArray>
    );
};

ListingAdditionalFeesField.propTypes = {
    name: PropTypes.string.isRequired,
    noteFieldName: PropTypes.string,
    noteValue: PropTypes.string,
    onNoteChange: PropTypes.func,
    fields: PropTypes.array.isRequired,
    showCredits: PropTypes.bool,
    showRegenerate: PropTypes.bool,
    showToggle: PropTypes.bool,
    toggleValue: PropTypes.string,
    enabled: PropTypes.bool,
    intl: intlShape.isRequired,
};

FeeModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
    usedFeeTypes: PropTypes.array.isRequired,
    feeTypeOptions: PropTypes.array.isRequired,
    whenToApplyOptions: PropTypes.array.isRequired,
    feeCalculationTypeOptions: PropTypes.array.isRequired,
    checkoutOptions: PropTypes.array.isRequired,
    intl: intlShape.isRequired,
    editingIdx: PropTypes.number,
};

export default injectIntl(ListingAdditionalFeesField);
